import React, { useEffect, useRef, useState } from "react";
import { PHOTOS, photoSrc, photoSrcSet } from "../content/photos";

const RATIOS = { "16/9": 16 / 9, "4/3": 4 / 3, "3/2": 3 / 2, "4/5": 4 / 5, "1/1": 1, "21/9": 21 / 9 };

/**
 * Cinematic photo block. Photography 70% / 3D accent 20% / motion 10%.
 * - reserves space via aspect-ratio (no layout shift)
 * - responsive srcset, lazy below the fold, auto WebP/AVIF via Unsplash
 * - entry: opacity 0→1, scale 1.03→1 (1000ms); hover: subtle zoom; parallax ≤ 12px (desktop only)
 * - treatment: dark cinematic contrast + soft warm accent; optional particle accent layer
 */
export function Photo({ name, ratio = "16/9", fill = false, priority = false, parallax = 10, particles = false, treat = "default", sizes, className = "", imgClassName = "", children }) {
  const p = PHOTOS[name];
  const ref = useRef(null); const moveRef = useRef(null);
  const [inView, setInView] = useState(priority);

  useEffect(() => {
    const el = ref.current; if (!el) return;
    const io = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setInView(true); io.disconnect(); } }, { threshold: 0.15, rootMargin: "80px" });
    io.observe(el); return () => io.disconnect();
  }, []);

  useEffect(() => {
    const el = ref.current; const mv = moveRef.current; if (!el || !mv || !parallax) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches || window.innerWidth < 768) return;
    let raf = 0;
    const update = () => {
      raf = 0;
      const r = el.getBoundingClientRect(); const vh = window.innerHeight;
      if (r.bottom < 0 || r.top > vh) return;
      const t = ((r.top + r.height / 2) - vh / 2) / vh; // -0.5..0.5
      mv.style.transform = `translate3d(0, ${Math.max(-parallax, Math.min(parallax, -t * parallax * 2))}px, 0)`;
    };
    const onScroll = () => { if (!raf) raf = requestAnimationFrame(update); };
    update(); window.addEventListener("scroll", onScroll, { passive: true }); window.addEventListener("resize", onScroll);
    return () => { window.removeEventListener("scroll", onScroll); window.removeEventListener("resize", onScroll); cancelAnimationFrame(raf); };
  }, [parallax]);

  if (!p) return null;
  const r = RATIOS[ratio] || 16 / 9;
  return (
    <div ref={ref} className={`photo ${fill ? "photo-fill" : ""} ${className}`} style={fill ? undefined : { aspectRatio: ratio.replace("/", " / ") }} data-testid={`photo-${name}`}>
      <div ref={moveRef} className="photo-move">
        <img src={photoSrc(p.id, 1440, fill ? undefined : Math.round(1440 / r))} srcSet={photoSrcSet(p.id, fill ? undefined : r)}
          sizes={sizes || (fill ? "100vw" : "(max-width: 768px) 100vw, 50vw")} alt={p.alt} loading={priority ? "eager" : "lazy"} decoding="async"
          fetchPriority={priority ? "high" : "auto"} style={{ objectPosition: p.pos }} className={`photo-img ${inView ? "is-in" : ""} ${imgClassName}`} />
      </div>
      {treat && <div className={`photo-treat photo-treat-${treat}`} aria-hidden="true" />}
      {particles && <div className="particles hidden md:block" aria-hidden="true" />}
      {children}
    </div>
  );
}
